/**
 * กระเด็น / ล้ม / ลุก on the figure (lane DUEL-ARMOR; rules packages/shared systems/duelKnock.ts). While the rules hold a
 * fighter thrown, down or getting up, this picks its receive frame (it wins over the ordinary hit reaction, see
 * render/hitReact.ts): thrown = over backwards through the air (`rcvFall` to its impact, lifted on an arc), tripped = the
 * same topple without the arc, down = the impact, bounce and lie, then the get-up (`rcvUpKip`: rise, rise guarding, roll,
 * rising attack; the roll and the rising attack reuse it until their own poses exist). Picture only. The views are fed once a
 * frame by render/passes/duelArmorTell.ts.
 */
import type { KnockView } from '@nine/shared';
import { eased, type Ease } from './motions/kit';
import { MOTIONS, RECEIVE_CHANNELS, type ReceiveMotionId } from './rig/receive';
import type { ReceiveFrame } from './rig/receivePlay';

const views = new Map<number, KnockView>();

/** The rules' knock state of a fighter this frame (null: none). */
export function setKnockView(id: number, v: KnockView | null): void {
  if (v) views.set(id, v);
  else views.delete(id);
}

/** The air arc's top in art pixels. */
const ARC = 14;
/** Down: the impact, bounce and lie play over this (s), then the lie holds. */
const LAND = 0.3;

function channel(vals: readonly number[] | undefined, ease: readonly Ease[], u: number): number {
  if (!vals?.length) return 0;
  const n = vals.length;
  const x = Math.max(0, Math.min(1, u)) * (n - 1);
  const i = Math.min(n - 2, Math.floor(x));
  const k = eased(ease[i] ?? 'inOut', x - i);
  return vals[i]! + (vals[i + 1]! - vals[i]!) * k;
}

function frame(id: ReceiveMotionId, u: number, lift = 0): ReceiveFrame {
  const m = MOTIONS.find((x) => x.id === id)!;
  const ch = RECEIVE_CHANNELS[id];
  const uu = Math.max(0, Math.min(1, u));
  return {
    id,
    u: uu,
    rot: channel(ch?.rot, m.ease, uu),
    lift: channel(ch?.lift, m.ease, uu) + lift,
    turn: Math.round(channel(ch?.turn, m.ease, uu)),
  };
}

/** The knock frame of a figure now, or null (not thrown, down or rising). */
export function knockReactFrame(id: number): ReceiveFrame | null {
  const v = views.get(id);
  if (!v) return null;
  const k = v.len > 0 ? Math.min(1, v.t / v.len) : 1;
  switch (v.phase) {
    case 'air':
      return frame('rcvFall', k * 0.5, Math.round(4 * ARC * k * (1 - k)));
    case 'trip':
      return frame('rcvFall', k * 0.5);
    case 'down':
      return frame('rcvFall', 0.5 + 0.5 * Math.min(1, v.t / LAND));
    case 'roll':
      return frame('rcvUpKip', 0.15 + 0.6 * k);
    case 'rise':
    case 'riseAttack':
      return frame('rcvUpKip', k);
  }
}
